/**
 * The shared vocabulary of the system.
 *
 * Everything here is a controlled list. Free text where one of these belongs is
 * how reports stop meaning anything six months later (CATALOGUE.md §5).
 */
/** Job lifecycle — DESIGN.md §3.1. Deliberately small; everything else is a flag. */
export const JOB_STATUSES = [
    'DRAFT',
    'NEW',
    'TRIAGED',
    'DISPATCHING',
    'ASSIGNED',
    'EN_ROUTE',
    'ON_SITE',
    'IN_PROGRESS',
    'PENDING_PARTS',
    'PENDING_CUSTOMER',
    'PENDING_ACCESS',
    'ON_HOLD',
    'WORK_DONE',
    'VERIFIED',
    'CLOSED',
    'CANCELLED',
    'DISPATCH_FAILED',
];
/** States in which SLA clocks stop running — SLA.md §4. */
export const PAUSED_STATUSES = [
    'PENDING_CUSTOMER',
    'PENDING_ACCESS',
    'PENDING_PARTS',
    'ON_HOLD',
];
/** A job in one of these is finished; no clock runs and no dispatch applies. */
export const TERMINAL_STATUSES = ['CLOSED', 'CANCELLED'];
/** Assignment lifecycle — DESIGN.md §3.2. */
export const ASSIGNMENT_STATUSES = [
    'PENDING',
    'SENT',
    'DELIVERED',
    'READ',
    'ACCEPTED',
    'DECLINED',
    'EXPIRED',
    'SUPERSEDED',
    'WITHDRAWN',
    'SEND_FAILED',
];
export const PRIORITIES = ['P1', 'P2', 'P3', 'P4'];
/**
 * ROLES.md — who can hold an account.
 *
 * Everybody here works for the business. Customers hold no account at all:
 * they receive their contract as a link in an email and sign it there, and
 * that link is the whole of their relationship with this system. There was
 * once a `client` role and a portal behind it; it is gone, because an account
 * that only ever showed somebody their own contract was a login, a password
 * reset, a session and an audit trail bolted onto a thing an email already did.
 *
 * CC followers likewise never log in; they hold a link.
 */
export const ROLES = [
    'admin',
    'dispatcher',
    'technician',
    'sales',
    /**
     * People, not money. The roster, attendance, leave and who is off sick.
     *
     * Deliberately holds no financial authority — an HR officer can see an
     * expense claim and cannot approve it. That is segregation of duties in its
     * plainest form: the person who manages somebody's employment should not
     * also be the person who authorises payments to them.
     */
    'hr',
    /**
     * Money, not people. Invoices, payments, arrears, suppliers and the reports
     * that go to a bank.
     *
     * The mirror of the rule above: an accountant approves expenses and never
     * touches the roster, so they cannot create an employee and pay them.
     */
    'accountant',
];
/**
 * Roles belonging to the business.
 *
 * Every role is a staff role now that customers hold no account. The alias is
 * kept because a good deal of code asks the question, and the day somebody
 * adds a non-staff role again this is where the answer belongs — better than
 * that code having quietly hardcoded `ROLES` and been wrong later.
 */
export const STAFF_ROLES = ROLES;
export function isStaff(role) {
    return STAFF_ROLES.includes(role);
}
/** NOTIFICATIONS.md §1. Only WhatsApp carries buttons; the rest carry a magic link. */
export const CHANNELS = ['whatsapp', 'sms', 'email'];
export const MESSAGE_STATUSES = [
    'PENDING',
    'SENT',
    'DELIVERED',
    'READ',
    'FAILED',
];
/** How a job ended. `NO_ACCESS` and `NO_FAULT_FOUND` are normal, and counted. */
export const JOB_OUTCOMES = [
    'COMPLETED',
    'NO_ACCESS',
    'NO_FAULT_FOUND',
    'DEFERRED',
];
/** CATALOGUE.md §6 — required when a technician cannot take a job. */
export const DECLINE_REASONS = [
    'ON_ANOTHER_JOB',
    'NO_TRANSPORT',
    'OUTSIDE_MY_ZONE',
    'NO_MATERIALS',
    'NO_TOOLS',
    'NOT_CERTIFIED',
    'OFF_SHIFT',
    'SICK',
    'WEATHER_UNSAFE',
    'SECURITY_CONCERN',
    'OTHER',
];
export function isPaused(status) {
    return PAUSED_STATUSES.includes(status);
}
export function isTerminal(status) {
    return TERMINAL_STATUSES.includes(status);
}
/** Active work: not finished, not a draft. What the dispatcher board shows. */
export function isActive(status) {
    return !isTerminal(status) && status !== 'DRAFT';
}
/**
 * CATALOGUE.md §5 — a dropdown, never free text.
 *
 * Free-text root causes cannot be counted, and "what breaks most often?" is the
 * question management asks first.
 */
export const ROOT_CAUSE_CODES = [
    // Not a fault at all
    'BILLING_UNPAID',
    'CLIENT_POWER_OFF',
    'CLIENT_EQUIPMENT_UNPLUGGED',
    'NO_FAULT_FOUND',
    'CLIENT_ERROR_WIFI_PASSWORD',
    // Client equipment
    'ONT_FAULTY',
    'ROUTER_FAULTY',
    'ROUTER_MISCONFIGURED',
    'POWER_ADAPTER_FAULTY',
    // Drop / last mile
    'DROP_CABLE_CUT',
    'DROP_CABLE_BENT',
    'CONNECTOR_DIRTY_DAMAGED',
    'FAT_PORT_FAULTY',
    // Distribution / backbone
    'FIBRE_CUT_VANDALISM',
    'FIBRE_CUT_ROADWORKS',
    'FIBRE_CUT_TREE_WEATHER',
    'FIBRE_CUT_ROAD_ACCIDENT',
    'POLE_COLLAPSED',
    'SPLITTER_FAULTY',
    'SPLICE_DEGRADED',
    // Power
    'POWER_OUTAGE_KPLC',
    'BATTERY_FAILURE',
    'GENERATOR_FAILURE',
    'SOLAR_FAILURE',
    // Core / upstream
    'POP_EQUIPMENT_FAILURE',
    'UPSTREAM_PROVIDER_OUTAGE',
    'CAPACITY_OVERSUBSCRIBED',
    'NOC_CONFIGURATION_ERROR',
    // Wireless
    'WIRELESS_INTERFERENCE',
    'LOS_OBSTRUCTED',
    'CPE_MISALIGNED',
    'WIFI_COVERAGE_INSUFFICIENT',
    // Security
    'THEFT_EQUIPMENT',
    'THEFT_CABLE',
    'DELIBERATE_DAMAGE',
];
// ═══════════════════════════════════════════════════════════════════════════
// Contracts — CONTRACTS.md
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Where a contract is in its life.
 *
 * Deliberately short, and deliberately *not* containing "active" or "expired".
 * Those two are facts about today's date against the term, so storing them
 * would mean a nightly job that flips rows and a contract that is expired in
 * the column and in force in the dates whenever that job misses a night. They
 * are computed — see `standingOf()` in contracts.ts.
 */
export const CONTRACT_STATUSES = [
    /** Being written. Nothing has left the building. */
    'DRAFT',
    /**
     * Finished, frozen, and waiting for somebody to sign for the business.
     *
     * The step this status was added for. The company's signature used to be
     * applied automatically at the moment of sending, which meant nobody
     * actually signed — the name of whoever was configured in settings appeared
     * on the document because a salesperson pressed Send. A signature that
     * nobody chose to make is not a signature, so signing became its own act
     * with its own status.
     */
    'AWAITING_COMPANY',
    /** The company has signed and the customer has been sent their link. */
    'AWAITING_CUSTOMER',
    /** Both parties have signed. From here the document is evidence, not a form. */
    'SIGNED',
    /** Cancelled before or after signature, with a reason and an audit trail. */
    'VOIDED',
    /** Replaced by a later version. The old one stays readable forever. */
    'SUPERSEDED',
];
/** A signed contract can never go back to being editable. */
export const FINAL_CONTRACT_STATUSES = [
    'SIGNED',
    'VOIDED',
    'SUPERSEDED',
];
/** Who put their name to it. Exactly two parties, by design. */
export const SIGNATURE_PARTIES = ['company', 'customer'];
// ═══════════════════════════════════════════════════════════════════════════
// Sales — SALES.md
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Where a lead came from. The single most valuable field on the record.
 *
 * "Which of the things we do actually brings customers?" cannot be answered by
 * a free-text box, and it is the question that decides where the marketing
 * money goes next month.
 */
export const LEAD_SOURCES = [
    'WALK_IN',
    'REFERRAL',
    'PHONE',
    'WHATSAPP',
    'FACEBOOK',
    'FIELD_CANVASS',
    'POSTER',
    'EVENT',
    'RESELLER',
    'WEBSITE',
    'EXISTING_CUSTOMER',
];
/**
 * The pipeline. Ordered, because the order is the whole point — a funnel
 * report counts how many fell out between each pair.
 *
 * `SURVEY_BOOKED` exists because this is an ISP and not a shop: somebody has
 * to stand outside the building and look for line of sight to a FAT before a
 * price can honestly be quoted.
 */
export const LEAD_STATUSES = [
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'SURVEY_BOOKED',
    'QUOTED',
    'CONTRACT_SENT',
    'WON',
    'LOST',
    'PARKED',
];
/** Nothing more happens to a lead in one of these without reopening it. */
export const CLOSED_LEAD_STATUSES = ['WON', 'LOST'];
/**
 * Why a lead was lost — a dropdown, for the same reason root causes are.
 *
 * `NO_COVERAGE` is the one that pays for itself: a map of the addresses that
 * wanted service and could not get it is a build plan.
 */
export const LOST_REASONS = [
    'PRICE',
    'NO_COVERAGE',
    'CHOSE_COMPETITOR',
    'MOVED_AWAY',
    'NO_RESPONSE',
    'NOT_READY_YET',
    'DUPLICATE',
    'OTHER',
];
/** What a salesperson did, so the pipeline has a history and not just a state. */
export const LEAD_ACTIVITY_KINDS = [
    'CALL',
    'WHATSAPP',
    'VISIT',
    'SURVEY',
    'QUOTE',
    'NOTE',
    'STATUS_CHANGE',
];
/**
 * A referral's journey to being worth money.
 *
 * `QUALIFIED` and `APPROVED` are separate on purpose. Qualifying is arithmetic
 * — the referred customer was installed and has been live long enough to count.
 * Approving is a person deciding to pay. Collapsing them would mean the system
 * paid rewards on its own.
 */
export const REFERRAL_STATUSES = [
    'PENDING',
    'QUALIFIED',
    'APPROVED',
    'PAID',
    'REJECTED',
];
/** What the referrer gets. Cash is deliberately last. */
export const REFERRAL_REWARD_KINDS = ['FREE_DAYS', 'BILL_CREDIT', 'DATA_BOOST', 'CASH'];
// ═══════════════════════════════════════════════════════════════════════════
// Client portal
// ═══════════════════════════════════════════════════════════════════════════
/** A problem a customer reported from their own page. */
export const PROBLEM_STATUSES = ['NEW', 'ACKNOWLEDGED', 'RESOLVED'];
/** What the customer says is wrong, before anyone has diagnosed it. */
export const PROBLEM_KINDS = [
    'NO_INTERNET',
    'SLOW',
    'INTERMITTENT',
    'WIFI_COVERAGE',
    'BILLING',
    'RELOCATION',
    'UPGRADE_REQUEST',
    'OTHER',
];
/**
 * Kinds of notification in the tray.
 *
 * `RELEASE` is the odd one out and the reason the tray exists at all beyond
 * alerts: it is how everybody finds out what changed in the application,
 * without an email nobody reads.
 */
export const NOTIFICATION_KINDS = [
    'RELEASE',
    'CONTRACT',
    'PROBLEM',
    'LEAD',
    'REFERRAL',
    'TICKET',
    'SYSTEM',
];
/** Grouped for a dropdown a technician can scan on a phone. */
export const ROOT_CAUSE_GROUPS = [
    { label: 'Not a fault', codes: ['BILLING_UNPAID', 'CLIENT_POWER_OFF', 'CLIENT_EQUIPMENT_UNPLUGGED', 'NO_FAULT_FOUND', 'CLIENT_ERROR_WIFI_PASSWORD'] },
    { label: 'Client equipment', codes: ['ONT_FAULTY', 'ROUTER_FAULTY', 'ROUTER_MISCONFIGURED', 'POWER_ADAPTER_FAULTY'] },
    { label: 'Drop / last mile', codes: ['DROP_CABLE_CUT', 'DROP_CABLE_BENT', 'CONNECTOR_DIRTY_DAMAGED', 'FAT_PORT_FAULTY'] },
    { label: 'Backbone', codes: ['FIBRE_CUT_VANDALISM', 'FIBRE_CUT_ROADWORKS', 'FIBRE_CUT_TREE_WEATHER', 'FIBRE_CUT_ROAD_ACCIDENT', 'POLE_COLLAPSED', 'SPLITTER_FAULTY', 'SPLICE_DEGRADED'] },
    { label: 'Power', codes: ['POWER_OUTAGE_KPLC', 'BATTERY_FAILURE', 'GENERATOR_FAILURE', 'SOLAR_FAILURE'] },
    { label: 'Core / upstream', codes: ['POP_EQUIPMENT_FAILURE', 'UPSTREAM_PROVIDER_OUTAGE', 'CAPACITY_OVERSUBSCRIBED', 'NOC_CONFIGURATION_ERROR'] },
    { label: 'Wireless', codes: ['WIRELESS_INTERFERENCE', 'LOS_OBSTRUCTED', 'CPE_MISALIGNED', 'WIFI_COVERAGE_INSUFFICIENT'] },
    { label: 'Security', codes: ['THEFT_EQUIPMENT', 'THEFT_CABLE', 'DELIBERATE_DAMAGE'] },
];
//# sourceMappingURL=vocabulary.js.map